import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';

import { filter, finalize } from 'rxjs';

import { ICreateWorkSpace } from '@app/features/workspace/interfaces/workspace.interface';
import { ListItemSelector } from '@shared/components/list-item-selector/list-item-selector';
import { TopNavbarService } from '@shared/services/topNavbar/topNavbar.service';
import { WorkSpaceService } from '@app/features/workspace/services/workspace.service';
import { WorkspaceModel } from '@app/features/workspace/models/workspace.model';
import { ClickOutside } from '@shared/directives/click-outside';
import { UserService } from '@app/features/profile/services/user.service';
import { AuthService } from '@app/features/auth/services/auth.service';
import { clearIssueFiltersFromSession } from '@common/session-storage.util';

@Component({
  standalone: true,
  selector: 'app-top-nav-bar',
  imports: [RouterModule, CommonModule, ListItemSelector, ClickOutside],
  templateUrl: './top-nav-bar.html',
  styleUrl: './top-nav-bar.scss'
})
export class TopNavBar implements OnInit {
  showThemesOptions = signal<boolean>(false);
  isShowWorkSpaces = signal<boolean>(false);
  isChangeTheme = signal('light');
  loader = signal<boolean>(false);
  active = signal<string>('');
  userData = signal<any>({});
  workSpaces = signal([]);
  constructor(
    public workSpaceService: WorkSpaceService,
    private navBarService: TopNavbarService,
    public userService: UserService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        if (url.includes('/projects')) {
          this.active.set('projects');
        } else if (url.includes('/members')) {
          this.active.set('members');
        } else if (url.includes('/clients')) {
          this.active.set('clients');
        } else if (url.includes('/apps')) {
          this.active.set('apps');
        } else if (url.includes('/settings')) {
          this.active.set('settings');
        }
      });
  }
  fullName = computed(() => {
    return `${this.userService.userDetail()?.first_name} ${this.userService.userDetail()?.last_name}`
  })
  showThemes(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.showThemesOptions.set(!this.showThemesOptions());
  }
  changeTheme(theme: string) {
    this.isChangeTheme.set(theme)
  }

  logOut(): void {
    this.authService.logOut().subscribe({
      next: ((res) => {
        if (res.success === true) {
          localStorage.clear();
          clearIssueFiltersFromSession();
          this.navBarService.topNavbar?.set(null)
          this.router.navigate(['/login']);

        }
      })
    })
  }
  getAllWorkSpaces() {
    this.loader.set(true)
    this.workSpaceService.getAllWorkspaces().pipe(finalize(() => { this.loader.set(false) })).subscribe({
      next: ((res) => {
        if (res.success === true) {
          this.workSpaceService.setWorkSpaceList(res.data)
          // this.workSpaces.set(res.data)
        }
      })
    })
  }
  showWorkSpaces() {
    this.isShowWorkSpaces.update(value => !value);
  }
  redirectToWorkSpace(event: any): void {
    if(event.action === 'select') {
    if (event.data.id !== this.workSpaceService.activeWorkspace().id) {
      this.workSpaceService.setActiveWorkspace(event.data);
      this.workSpaceService.switchWorkSpace({ tenant_id: event.data.id }).subscribe({
        next: ((res: ICreateWorkSpace) => {
          if (res.success === true) {
            localStorage.setItem('workspace_token', res?.data?.workspace_token);
            this.isShowWorkSpaces.set(false);
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.router.navigate([`/workspace/${event.data.id}/projects`]);
            });
          }
        })
      })
    }
   } else if (event.action === 'add') {
      this.router.navigate(['/workspace/create']);
    }
  }
  redirectTo(path: string): void {
    const workspaceId =
      this.route.snapshot.paramMap.get('id') || this.workSpaceService.activeWorkspace()?.id;
    const targetUrl = `/workspace/${workspaceId}/${path}`;
    const currentPath = this.router.url.split('?')[0];

    // Same-section re-click does not navigate; notify the page to reset local UI (open forms).
    if (currentPath === targetUrl) {
      this.navBarService.notifySectionReclick();
    } else {
      this.router.navigateByUrl(targetUrl);
    }
    this.active.set(path);
  }
  isActive(path: string): boolean {
    return this.router.url.includes(path);
  }
  editWorkspace(workspaceId){
     this.router.navigate(['workspace', workspaceId, 'edit']);
  }
  ngOnInit(): void {
    this.getAllWorkSpaces()
  }
}
